import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FaCaretDown, FaChevronRight, FaSyncAlt } from 'react-icons/fa'
import { Button } from '@components'
import type { IMcpConnection } from '@query'
import { ToolPermissionControl } from './ToolPermissionControl'
import {
  ApprovalPatch,
  PermState,
  ToolGroupKey,
  commonState,
  computePatch,
  deriveStates,
  groupTools,
  statesEqual,
  toolDisplayName
} from './toolPermissions'

interface Props {
  connection: IMcpConnection
  saving: boolean
  loadingTools: boolean
  onSave: (patch: ApprovalPatch) => void
  onLoadTools: () => void
}

// Per-tool permission config for a user-connected MCP, grouped by the standard read-only hint.
export const ToolApprovalPanel: React.FC<Props> = ({
  connection,
  saving,
  loadingTools,
  onSave,
  onLoadTools
}) => {
  const [t] = useTranslation()
  const available = connection.available_tools ?? []
  const initial = useMemo(() => deriveStates(connection), [connection])
  const [states, setStates] = useState<Record<string, PermState>>(initial)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  // Resync when the server-side config changes (after a save or a tools reload).
  useEffect(() => setStates(initial), [initial])

  const groups = useMemo(() => groupTools(available), [available])
  const onlyOther = groups.length === 1 && groups[0].key === 'other'
  const dirty = useMemo(() => !statesEqual(states, initial), [states, initial])

  const setTool = (name: string, state: PermState) =>
    setStates(prev => ({ ...prev, [name]: state }))

  const setGroup = (names: string[], state: PermState) =>
    setStates(prev => {
      const next = { ...prev }
      for (const name of names) next[name] = state
      return next
    })

  if (available.length === 0) {
    return (
      <Panel>
        <Hint>{t('lucaSettings.approval.noTools')}</Hint>
        <Footer>
          <Button
            isSecondary
            size="sm"
            icon={<FaSyncAlt />}
            showSpinner={loadingTools}
            onClick={onLoadTools}
            disabled={loadingTools}
          >
            {t('lucaSettings.approval.loadTools')}
          </Button>
        </Footer>
      </Panel>
    )
  }

  const groupLabel = (key: ToolGroupKey) =>
    onlyOther ? t('lucaSettings.approval.group.tools') : t(`lucaSettings.approval.group.${key}`)

  return (
    <Panel>
      <Header>
        <Title>{t('lucaSettings.approval.permissionsTitle')}</Title>
        <Subtitle>{t('lucaSettings.approval.permissionsHint')}</Subtitle>
      </Header>

      {groups.map(group => {
        const names = group.tools.map(tool => tool.name)
        const isCollapsed = Boolean(collapsed[group.key])
        return (
          <Group key={group.key}>
            <GroupHeader>
              <GroupToggle
                type="button"
                onClick={() => setCollapsed(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
              >
                {isCollapsed ? <FaChevronRight /> : <FaCaretDown />}
                <GroupName>{groupLabel(group.key)}</GroupName>
                <GroupCount>{group.tools.length}</GroupCount>
              </GroupToggle>
              <ToolPermissionControl
                value={commonState(group.tools, states)}
                onChange={state => setGroup(names, state)}
              />
            </GroupHeader>
            {!isCollapsed && (
              <ToolList>
                {group.tools.map(tool => (
                  <ToolRow key={tool.name}>
                    <ToolText
                      title={tool.description ? `${tool.name} — ${tool.description}` : tool.name}
                    >
                      <ToolName>{toolDisplayName(tool)}</ToolName>
                      {tool.title ? <ToolId>{tool.name}</ToolId> : null}
                    </ToolText>
                    <ToolPermissionControl
                      value={states[tool.name] ?? 'allow'}
                      onChange={state => setTool(tool.name, state)}
                    />
                  </ToolRow>
                ))}
              </ToolList>
            )}
          </Group>
        )
      })}

      <Footer>
        <Button
          intent="primary"
          size="sm"
          showSpinner={saving}
          onClick={() => onSave(computePatch(available, states))}
          disabled={saving || !dirty}
        >
          {t('lucaSettings.approval.save')}
        </Button>
      </Footer>
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme }) => theme.luca.mist};
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const Title = styled.div`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.luca.navy};
`

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.luca.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
`

const Hint = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.4;
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

// A subtle full-width band so each tool group is obviously a section, even while scrolling a long
// list — clearly distinct from the individual tool rows below it.
const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => `color-mix(in srgb, ${theme.luca.navy} 8%, ${theme.luca.mist})`};
`

const GroupToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colors.neutralBlack};

  &:focus,
  &:focus-visible {
    outline: none;
  }

  svg {
    width: 0.7rem;
    height: 0.7rem;
    color: ${({ theme }) => theme.colors.iconGray};
  }
`

const GroupName = styled.span`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.iconGrayDark};
`

const GroupCount = styled.span`
  min-width: 1.2rem;
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => `color-mix(in srgb, ${theme.colors.iconGray} 22%, white)`};
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  text-align: center;
`

const ToolList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${({ theme }) => theme.spacing.sm}rem;
`

const ToolRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem 0;
  border-top: 1px solid ${({ theme }) => `color-mix(in srgb, ${theme.colors.iconGray} 13%, white)`};

  &:first-child {
    border-top: none;
  }
`

const ToolText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const ToolName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ToolId = styled.div`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`
